<template>
  <div class="flex items-center shrink-0">
    <button
      type="button"
      class="bg-surface-canvas border border-border-level-300 cursor-pointer dark:bg-surface-level-800/90 dark:border-border-inverse/20 dark:hover:bg-surface-level-800 dark:hover:border-primary dark:hover:text-content-inverse dark:shadow-search-trigger-inset dark:text-content-level-200 focus-visible:ring-2 focus-visible:ring-primary/30 gap-2 h-8 hover:bg-surface-level-50 hover:border-border-level-300 hover:text-content-level-900 inline-flex items-center justify-center lg:min-w-[5.25rem] min-w-[4.25rem] outline-hidden px-3 rounded-md shadow-xs text-content-level-500 transition-colors"
      :aria-label="$t('dashboard.actions.openSearch')"
      :title="
        $t('dashboard.messages.searchShortcutTitle', {
          shortcut: modifierKeyLabel
        })
      "
      @click="openSearch"
    >
      <MagnifyingGlassIcon
        class="shrink-0 size-[1.125rem] text-primary"
        aria-hidden="true"
      />

      <kbd
        class="bg-surface-level-50/90 border border-border-level-200/80 dark:bg-surface-canvas/10 dark:border-border-inverse/20 dark:text-content-level-300 font-medium font-sans hidden lg:inline pointer-events-none px-1.5 py-0.5 rounded select-none text-[10px] text-content-level-400 tracking-wide"
      >
        {{ modifierKeyLabel }}
      </kbd>
    </button>

    <BaseModal
      :model-value="open"
      :title="$t('dashboard.actions.openSearch')"
      max-width-class="max-w-2xl"
      panel-class="overflow-hidden self-start sm:mt-20"
      @update:model-value="closeSearch"
      @after-leave="resetPanel"
    >
      <template #content>
        <UCommandPalette
          v-model:search-term="query"
          :groups="paletteGroups"
          :loading="loading"
          :placeholder="searchPlaceholder"
          :input="searchInputProps"
          :ui="paletteUi"
          class="max-h-[28rem]"
        >
          <template #item-leading="{ item }">
            <component
              :is="iconForKind(kindOfItem(item))"
              class="flex-none size-6 text-content-level-500"
              aria-hidden="true"
            />
          </template>

          <template #item-trailing="{ item }">
            <span
              class="capitalize dark:text-content-level-400 flex-none text-content-level-500 text-xs"
            >
              {{ kindLabel(kindOfItem(item)) }}
            </span>
          </template>

          <template #empty>
            <p
              v-if="panelState === 'searching'"
              class="dark:text-content-level-400 text-content-level-500 text-sm"
            >
              {{ $t('dashboard.labels.searching') }}
            </p>

            <p
              v-else-if="panelState === 'failed'"
              class="dark:text-danger-400 text-danger-600 text-sm"
            >
              {{ fetchError.message.value }}
            </p>

            <p
              v-else-if="searchApiReady"
              class="dark:text-content-level-300 text-content-level-900 text-sm"
            >
              {{ $t('dashboard.messages.noMatchesForThatSearchTryADifferentTerm') }}
            </p>

            <p
              v-else
              class="dark:text-content-level-300 text-content-level-900 text-sm"
            >
              {{
                $t('dashboard.messages.searchIsNotAvailableUntilTheDashboardSearchAPIIsConfigured')
              }}
            </p>
          </template>
        </UCommandPalette>
      </template>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
import type { CommandPaletteGroup, CommandPaletteItem } from '@nuxt/ui'
import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid'
import {
  BanknotesIcon,
  BuildingStorefrontIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  TagIcon,
  UserIcon
} from '@heroicons/vue/24/outline'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDebouncer } from '@tanstack/vue-pacer'
import {
  DASHBOARD_SEARCH_API_PATH,
  DASHBOARD_SEARCH_PER_TYPE_DEFAULT,
  DASHBOARD_SEARCH_Q_MIN
} from '~/composables/dashboardSearch.constants'
import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import { Normalization } from '~/utils/normalization'
import { clampDashboardSearchQuery } from '~/composables/clampDashboardSearchQuery'
import { groupDashboardSearchResults } from '~/composables/groupDashboardSearchResults'
import { normalizeDashboardSearchResults } from '~/composables/normalizeDashboardSearchResults'
import type { DashboardSearchKind } from '~/composables/DashboardSearchKind'
import type { DashboardSearchResult } from '~/composables/DashboardSearchResult'
import { useUserStore } from '~/stores/useUserStore'
import { useLogger } from '~/composables/useLogger'
import { useLocalizedMessage } from '~/composables/useLocalizedMessage'
import { useIdentity } from '~/composables/useIdentity'
import { hasText } from '~/utils/hasText'
import { holdsCustomerRole } from '~/utils/holdsCustomerRole'
import { resolveRoleNames } from '~/utils/resolveRoleNames'
import { DEBOUNCE_MS } from '~/constants/debounce.constants'

const logger = useLogger('DashboardGlobalSearch')

const { identity } = useIdentity()
const userStore = useUserStore()
const { t } = useI18n()

const isCustomerRole = computed(() => {
  const resolvedUser = identity.value ?? userStore.user
  return holdsCustomerRole(resolveRoleNames(resolvedUser))
})

const isMac = ref(true)

const modifierKeyLabel = computed(() => (isMac.value ? '⌘K' : 'Ctrl K'))

const searchApiReady = Boolean(DASHBOARD_SEARCH_API_PATH)

const open = ref(false)
const query = ref('')
const queryTrimmed = computed(() => query.value.trim())
const queryMeetsMinLength = computed(() => queryTrimmed.value.length >= DASHBOARD_SEARCH_Q_MIN)
const results = ref<DashboardSearchResult[]>([])
const groupedResults = computed(() => groupDashboardSearchResults(results.value))
const loading = ref(false)
const fetchError = useLocalizedMessage()
let searchSeq = 0
const searchDebouncer = useDebouncer(
  (q: string) => {
    void runSearch(q)
  },
  { wait: DEBOUNCE_MS.search }
)

const client = useApiRequest()

type SearchPanelState = 'idle' | 'searching' | 'failed' | 'noMatches' | 'results'

const SEARCH_INPUT_MAX_LENGTH = 128

const searchInputProps = { autocomplete: 'off', maxlength: SEARCH_INPUT_MAX_LENGTH } as const

const searchPlaceholder = computed(() =>
  isCustomerRole.value
    ? t('dashboard.messages.findYourOrdersAndStores')
    : t('dashboard.messages.searchOrdersStoresUsersAndMore')
)

const hasFetchError = computed(() => hasText(fetchError.message.value))

const panelState = computed<SearchPanelState>(() => {
  if (!queryMeetsMinLength.value) return 'idle'
  if (loading.value) return 'searching'
  if (hasFetchError.value) return 'failed'
  return groupedResults.value.length > 0 ? 'results' : 'noMatches'
})

const paletteUi = computed(() => ({ empty: panelState.value === 'idle' ? 'hidden' : '' }))

const distinctSubtitle = (row: DashboardSearchResult): string =>
  hasText(row.subtitle) && (!hasText(row.category) || row.subtitle !== row.category)
    ? row.subtitle
    : ''

const paletteItem = (row: DashboardSearchResult): CommandPaletteItem => ({
  description: distinctSubtitle(row),
  id: row.dedupeKey,
  label: row.name,
  onSelect: () => {
    selectResult(row)
  }
})

const kindByItemId = computed(
  () => new Map(results.value.map((row) => [row.dedupeKey, row.kind] as const))
)

const kindOfItem = (item: CommandPaletteItem): DashboardSearchKind | undefined => {
  const id: unknown = item['id']
  return typeof id === 'string' ? kindByItemId.value.get(id) : undefined
}

const paletteGroups = computed<CommandPaletteGroup[]>(() =>
  panelState.value === 'results'
    ? groupedResults.value.map((group) => ({
        id: group.category,
        ignoreFilter: true,
        items: group.items.map(paletteItem),
        label: group.category
      }))
    : []
)

function openSearch() {
  open.value = true
}

function closeSearch() {
  open.value = false
}

function iconForKind(kind: DashboardSearchKind | undefined) {
  switch (kind) {
    case 'order':
      return ClipboardDocumentListIcon
    case 'store':
      return BuildingStorefrontIcon
    case 'menu':
      return CubeIcon
    case 'brand':
      return TagIcon
    case 'user':
      return UserIcon
    case 'payout':
      return BanknotesIcon
    default:
      return MagnifyingGlassIcon
  }
}

const kindLabelResolvers = {
  brand: () => t('dashboard.labels.brand'),
  default: () => t('dashboard.labels.open'),
  menu: () => t('dashboard.labels.menuSingular'),
  order: () => t('dashboard.labels.order'),
  payout: () => t('dashboard.labels.payoutSingular'),
  store: () => t('dashboard.fields.store'),
  user: () => t('dashboard.labels.userSingular')
} satisfies Record<DashboardSearchKind, () => string>

function kindLabel(kind: DashboardSearchKind | undefined) {
  return kindLabelResolvers[kind ?? 'default']()
}

function resetPanel() {
  query.value = ''
  results.value = []
  fetchError.clear()
  loading.value = false
}

async function runSearch(q: string) {
  if (!DASHBOARD_SEARCH_API_PATH) {
    results.value = []
    loading.value = false
    return
  }

  const qClamped = clampDashboardSearchQuery(q)
  if (!qClamped || qClamped.length < DASHBOARD_SEARCH_Q_MIN) {
    results.value = []
    loading.value = false
    return
  }

  const seq = ++searchSeq
  loading.value = true
  fetchError.clear()

  try {
    const base = useRuntimeConfig().public.apiBaseUrl ?? ''
    const url = `${base}${DASHBOARD_SEARCH_API_PATH.startsWith('/') ? '' : '/'}${DASHBOARD_SEARCH_API_PATH}`
    const raw = await client(url, {
      method: 'GET',
      params: { per_type: DASHBOARD_SEARCH_PER_TYPE_DEFAULT, q: qClamped }
    })
    if (seq !== searchSeq) return
    const payload = Normalization.isRecord(raw) && 'data' in raw ? raw['data'] : raw
    results.value = normalizeDashboardSearchResults(payload)
  } catch (e) {
    if (seq !== searchSeq) return
    logger.error('runSearch', e)
    results.value = []
    fetchError.set(() => t('dashboard.messages.searchFailedTryAgainInAMoment'))
  } finally {
    if (seq === searchSeq) loading.value = false
  }
}

watch(
  () => [open.value, queryTrimmed.value] as const,
  ([isOpen, q]) => {
    searchDebouncer.cancel()
    // A request still in flight answers a query or palette state that is no longer current.
    searchSeq++
    if (!isOpen) return
    if (!q || q.length < DASHBOARD_SEARCH_Q_MIN) {
      results.value = []
      fetchError.clear()
      loading.value = false
      return
    }
    loading.value = true
    searchDebouncer.maybeExecute(q)
  }
)

function selectResult(row: DashboardSearchResult) {
  if (!hasText(row.url)) return
  open.value = false
  void navigateTo(row.url)
}

function onGlobalKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault()
    open.value = !open.value
  }
}

onMounted(() => {
  isMac.value = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)
  window.addEventListener('keydown', onGlobalKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
  searchSeq++
})
</script>
